import React, { useMemo } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import type { PluggableList } from 'react-markdown/lib/react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import { InlineReferenceLink } from '~/components/AiChat/components/Message/InlineReferenceLink';
import type { AiChatReference } from '~/components/AiChat/types';
import { useHasClaimReferences } from '~/components/Clive/ClaimReferences/ClaimReferencesProvider';

import { MarkdownRendererContext } from './MarkdownRendererContext';

import 'katex/dist/katex.min.css';

/**
 * CliveMarkdownDisplay
 *
 * A standardized markdown renderer for displaying formatted text content across the application.
 *
 * Features:
 * - remarkGfm (GitHub Flavored Markdown): Adds support for common markdown extensions including:
 *   - Tables
 *   - Strikethrough text (~~text~~)
 *   - Task lists (- [ ] and - [x])
 *   - Autolinks (URLs become clickable automatically)
 *   - Footnotes
 * - remarkMath + rehypeKatex: LaTeX maths from the LLM ($$…$$, or $…$ / \(…\) / \[…\] holding a command)
 *   renders as typeset maths instead of raw markup
 * - Automatic reference link handling: When references are provided, markdown links are
 *   automatically enhanced to support document references and web link previews
 *
 * Without remarkGfm, ReactMarkdown only supports basic CommonMark syntax (headings, lists, links, etc.)
 * but lacks these enhanced features that are commonly expected in modern markdown rendering.
 */

interface CliveMarkdownDisplayProps {
  content: string;
  className?: string;
  /**
   * Optional references for AI chat document and web link handling.
   * When provided, links in the markdown will automatically use InlineReferenceLink
   * for enhanced link rendering (document previews, web link cards, etc.)
   */
  references?: Record<string, AiChatReference>;
  /**
   * Custom component overrides for ReactMarkdown.
   * Use this to customize how specific markdown elements are rendered.
   * Default paragraph spacing (mb-8 last:mb-0) will be merged with any custom p component.
   * Note: If you provide a custom 'a' component, it will override the default reference link handling.
   */
  components?: Components;
}

// Hoisted so the plugins array keeps a stable identity across renders — a fresh array each render
// would make react-markdown re-parse the content even when nothing changed.
// Single `$` stays plain text so currency ("$500 to $600") never turns into maths; LLM LaTeX is
// promoted to `$$` by `promoteLatex` instead.
const REMARK_PLUGINS = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]] as PluggableList;
const REHYPE_PLUGINS = [[rehypeKatex, { throwOnError: false }]] as PluggableList;

// LLM LaTeX in `$…$`, `\(…\)` or `\[…\]`; a span counts only when it holds a command such as \frac,
// so currency and escaped markdown brackets stay plain text. A body never crosses its own closing
// delimiter, and a `$` followed by a digit is an amount, not a closing delimiter (Pandoc's rule). Failing
// that, a formula that doesn't itself open on an amount may hold amounts, as in `$\min($6, $1)$`; its
// closing `$` must not be followed by a command, which would be the next formula's opener.
const LATEX_SPAN =
  /\\\(((?:(?!\\\))[^\n])*?\\[a-zA-Z](?:(?!\\\))[^\n])*?)\\\)|\\\[((?:(?!\\\])[\s\S])*?\\[a-zA-Z](?:(?!\\\])[\s\S])*?)\\\]|(?<!\$)\$(?!\$)(?:([^$\n]*?\\[a-zA-Z][^$\n]*?)\$(?![$\d])|((?!\d)(?:[^$\n]|\$(?=\d))*?\\[a-zA-Z](?:[^$\n]|\$(?=\d))*?)\$(?![$\d]|\\[a-zA-Z]))/g;
// remark-math doesn't parse inside code, so code is left exactly as written.
const CODE = /(```[\s\S]*?```|`[^`\n]*`)/;
// In TeX `%` comments out the rest of the line. A `$` inside a `$…$` body can only be an amount; one
// inside `\(…\)` or `\[…\]` may be maths nested in `\text{}`, so it is left alone there.
const UNESCAPED_PERCENT = /(?<!\\)((?:\\\\)*)(%)/g;
const UNESCAPED_PERCENT_OR_DOLLAR = /(?<!\\)((?:\\\\)*)([$%])/g;

const promoteLatex = (content: string): string =>
  content
    .split(CODE)
    .map((part, i) =>
      i % 2
        ? part
        : part.replace(LATEX_SPAN, (_match, paren, bracket, dollar, dollarWithAmounts) => {
            const dollarBody = dollar ?? dollarWithAmounts;
            const tex =
              dollarBody === undefined
                ? (paren ?? bracket).replace(UNESCAPED_PERCENT, '$1\\$2')
                : dollarBody.replace(UNESCAPED_PERCENT_OR_DOLLAR, '$1\\$2');
            return `$$${tex}$$`;
          })
    )
    .join('');

const CliveMarkdownDisplay: React.FC<CliveMarkdownDisplayProps> = ({
  content,
  className = '',
  references,
  components: customComponents,
}) => {
  // Memoize the component map so its identity is stable across renders. Combined with React.memo
  // below, this stops react-markdown from re-parsing unchanged `content` every time an unrelated
  // state change (e.g. a streamed agent progress event) re-renders an ancestor.
  // A claim's reference cache, where one is mounted, resolves `src:<kind>:` citations the caller has
  // no map for — the conductor's text arrives unresolved, so its surfaces mount one.
  const hasClaimReferences = useHasClaimReferences();

  const mergedComponents = useMemo<Components>(() => {
    const defaultComponents: Components = {
      p: ({ children }) => <p className="mb-8 mt-12 last:mb-0">{children}</p>,
      // GFM tables arrive unstyled: the browser default draws no borders and no cell padding, so a
      // wide table reads as one run of text. A bordered grid with a header band, horizontally
      // scrollable rather than wrapped, so the text next to it keeps its width. react-markdown
      // hands a column's `| ---: |` alignment to `th`/`td` as `style.textAlign`, so `style` is
      // forwarded and wins over the left-aligned default.
      table: ({ children }) => (
        <div className="my-8 overflow-x-auto">
          <table className="w-full border-collapse text-sm">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
      th: ({ children, style }) => (
        <th
          style={style}
          className="border border-solid border-slate-300 px-8 py-4 text-left align-bottom font-semibold"
        >
          {children}
        </th>
      ),
      td: ({ children, style }) => (
        <td style={style} className="border border-solid border-slate-300 px-8 py-4 align-top">
          {children}
        </td>
      ),
    };

    // Add reference link handling when a map is provided or a cache can resolve one, and no custom
    // 'a' component is specified
    if ((references || hasClaimReferences) && !customComponents?.a) {
      defaultComponents.a = (props) => {
        // Extract href from either node properties or props (handles ReactMarkdown internal structure)
        const href = typeof props.node?.properties?.href === 'string' ? props.node.properties.href : props.href;
        return <InlineReferenceLink {...props} href={href} references={references} />;
      };
    }

    // Merge default components with custom components, custom components take precedence
    return {
      ...defaultComponents,
      ...customComponents,
    };
  }, [references, hasClaimReferences, customComponents]);

  const mathContent = useMemo(() => promoteLatex(content), [content]);

  // What renders inside a citation (a source text dialog) takes the display from here. The memoized
  // component is a module-level reference, so the context value is the same on every render.
  return (
    <MarkdownRendererContext.Provider value={MemoizedCliveMarkdownDisplay}>
      <div className={className}>
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mergedComponents}>
          {mathContent}
        </ReactMarkdown>
      </div>
    </MarkdownRendererContext.Provider>
  );
};

const MemoizedCliveMarkdownDisplay = React.memo(CliveMarkdownDisplay);

export default MemoizedCliveMarkdownDisplay;
